import { useNavigate } from "@tanstack/react-router";
import { SCORE_TIER_HIGH, SCORE_TIER_MID } from "@/lib/scoreTiers";
import { AlertTriangle, BookOpen, FileText, Mail, Trash2 } from "lucide-react";

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ScoringRing({ scored, total }: { scored: number; total: number }) {
  const pct = total > 0 ? scored / total : 0;
  const r = 10;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="inline-block">
      <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" strokeWidth="2" className="text-muted" />
      <circle
        cx="12" cy="12" r={r} fill="none" stroke="#a6e3a1" strokeWidth="2"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform="rotate(-90 12 12)" strokeLinecap="round"
      />
    </svg>
  );
}
function ScoringFingerprint({ buckets }: { buckets: number[] }) {
  const max = Math.max(...buckets, 1);
  const w = 120;
  const h = 32;
  const bucketW = w / buckets.length;

  // Build smooth bezier path through bucket tops
  const points = buckets.map((v, i) => ({
    x: i * bucketW + bucketW / 2,
    y: h - (v / max) * (h - 4) - 2,
  }));

  // Simple catmull-rom to bezier for smooth curve
  let d = `M0,${h} L${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  d += ` L${w},${h} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="inline-block">
      <defs>
        <linearGradient id="fg-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#9CA3AF" stopOpacity="0.3" />
          <stop offset="50%" stopColor="#1D9E75" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#1D9E75" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <path d={d} fill="url(#fg-grad)" />
      <path d={d.replace(/ L\d+,\d+ Z$/, "")} fill="none" stroke="#1D9E75" strokeWidth="1.5" opacity="0.6" />
    </svg>
  );
}

function CompletenessBar({ score }: { score: number }) {
  const color = score >= SCORE_TIER_HIGH ? "#1D9E75" : score >= SCORE_TIER_MID ? "#BA7517" : "#D85A30";
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score}%`, background: color }}
        />
      </div>
      <span className="text-[10px] font-medium" style={{ color }}>
        {score}%
      </span>
    </div>
  );
}

import { useState } from "react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useConceptList } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { ConceptListItem } from "@/types/concept";
import { DeleteConceptDialog } from "./DeleteConceptDialog";

const ANGLES_SHOWN = 2;

/** "3 angles · Sleep · Adolescents · +1" — only for concepts with 2+ angles. */
function AngleLine({ labels }: { labels: string[] }) {
  const extra = labels.length - ANGLES_SHOWN;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mt-1 flex min-w-0 items-center gap-1 text-xs">
            <span className="shrink-0 font-medium text-foreground">{labels.length} angles</span>
            {labels.slice(0, ANGLES_SHOWN).map((label, i) => (
              <span
                key={i}
                className="min-w-0 truncate rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
              >
                {label}
              </span>
            ))}
            {extra > 0 && <span className="shrink-0 text-muted-foreground">+{extra}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <ul>
            {labels.map((label, i) => <li key={i}>{label}</li>)}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ConceptList() {
  const { data: concepts, isLoading, isError, refetch } = useConceptList();
  const { data: currentUser } = useCurrentUser();
  const digestFrequency = currentUser?.digest_frequency ?? "";
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<ConceptListItem | null>(
    null,
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-lg" />
        ))}
      </div>
    );
  }

  // A failed load has no data; without this it fell through to the empty
  // state, which reads as "your concepts are gone" (2026-09-30, CORS preview).
  if (isError && !concepts) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
        <AlertTriangle className="size-10" />
        <p>Couldn't load your concepts. Check your connection and try again.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!concepts?.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <BookOpen className="size-10" />
        <p>No concepts yet. Create one to get started.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {concepts.map((concept) => (
          <Card
            key={concept.id}
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() =>
              navigate({ to: "/concepts/$conceptId/curate", params: { conceptId: concept.id } })
            }
          >
            <CardHeader className="relative">
              <CardTitle className="pr-8 text-lg">{concept.name}</CardTitle>
              <CardDescription className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <FileText className="size-3" />
                  {concept.foundational_count} seed
                </span>
                <span>{concept.trawled_count} trawled</span>
              </CardDescription>
              {currentUser?.features?.facets && (concept.angle_labels?.length ?? 0) >= 2 && (
                <AngleLine labels={concept.angle_labels!} />
              )}
              <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ScoringRing scored={concept.scored_count} total={concept.foundational_count + concept.trawled_count} />
                  {concept.scored_count} scored
                </span>
                {concept.unread_count > 0 && (
                  <span>{concept.unread_count} unread</span>
                )}
                {(concept.monitor_recent_count > 0 || concept.last_monitor_run) && (
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "#f59e0b" }} />
                    Living Lit: {concept.last_monitor_run
                      ? <>{concept.monitor_last_run_count} from last run</>
                      : <>{concept.monitor_recent_count} in last 7 days</>}
                    {concept.last_monitor_run && concept.monitor_recent_count > concept.monitor_last_run_count && (
                      <> &middot; {concept.monitor_recent_count} in 7 days</>
                    )}
                    {concept.untriaged_monitor_count > 0 && (
                      <> &middot; <span className="font-medium text-green-400">{concept.untriaged_monitor_count} to triage</span></>
                    )}
                  </span>
                )}
              </div>
              {concept.score_distribution && concept.scored_count > 5 && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground/60">Score distribution:</span>
                  <ScoringFingerprint buckets={concept.score_distribution} />
                </div>
              )}
              {concept.completeness_score != null && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="text-[10px] text-muted-foreground/60">Argument completeness:</span>
                  <CompletenessBar score={concept.completeness_score} />
                </div>
              )}
              <p className="mt-1 text-[10px] text-muted-foreground/60">
                Last active: {formatRelativeTime(concept.updated_at)}
              </p>
              {concept.setup_completed_at === null && currentUser?.features?.setup_v2 && (
                <Button
                  size="sm"
                  className="mt-2 w-fit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({ to: "/concepts/new", search: { resume: concept.id } });
                  }}
                >
                  Finish setup
                </Button>
              )}
              {concept.monitor_enabled &&
                (!digestFrequency || digestFrequency === "off") &&
                (Date.now() - new Date(concept.created_at).getTime()) > 3 * 24 * 60 * 60 * 1000 && (
                <div className="mt-1 flex items-center gap-1 rounded bg-blue-500/10 px-1.5 py-0.5 text-[0.6rem] text-blue-400">
                  <Mail className="size-2.5" />
                  Set up email digests
                </div>
              )}
              <button
                className="absolute right-4 top-4 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(concept);
                }}
              >
                <Trash2 className="size-4" />
              </button>
            </CardHeader>
          </Card>
        ))}
      </div>

      <DeleteConceptDialog
        concept={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}
